// frontend/src/tabs/useTabPersistedState.js
//
// `useState` replacement that persists state to sessionStorage keyed by
// the tab id this component belongs to. Used by per-page filter slots
// so two tabs at the same path can hold independent filter selections
// across F5 (the canonical "two Inventory tabs, different locations"
// use case).
//
// Tab identity is read from TabIdContext, which TabsHost provides around
// each rendered tab. Outside that provider the hook throws — silent
// fallback to "no persistence" would mask a real wiring bug.

import { useContext, useEffect, useRef, useState } from 'react';
import { TabIdContext } from './tabsContexts';
import { loadTabSlot, saveTabSlot } from './storage';

export function useTabPersistedState(key, initialValue) {
  const tabId = useContext(TabIdContext);
  if (!tabId) {
    throw new Error(
      'useTabPersistedState must be used inside a TabIdContext.Provider ' +
      '(rendered by TabsHost). Calling it outside the tabbed shell is a bug.',
    );
  }

  // Read once on mount — subsequent renders use the in-memory React state.
  // The tab id is stable for the lifetime of a tab (TabsHost gives each
  // tab a stable id), so we don't need to re-read on tabId changes.
  const [value, setValue] = useState(() => {
    const stored = loadTabSlot(tabId, key);
    return stored === undefined ? initialValue : stored;
  });

  // Skip the first effect run (initial value already came from storage
  // via useState's initializer). After that, every change writes through.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    saveTabSlot(tabId, key, value);
  }, [tabId, key, value]);

  return [value, setValue];
}
